import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import Table from '../../../../components/organisms/Table';
import UnderRow from './UnderRow';
import { DOMAIN_TYPES, STATUSES, TABLE_KEYS, TTypeEnum, VALIDATION_MESSAGES } from './consts';
import { getAlertMessage, tableColumns } from './tableUtils';
import css from './SubDomainLinkTable.css';
import Icon from '../../../../components/foundations/Icon';
import { route } from '../../../../utils/routerUtils';

const DNS_ZONE = 'sng.link';
const SUBDOMAIN_REGEX = /^(?![^-\n]*(?:-(?!\.)[^-\n]*)*-\.)(?:(?!-)[A-Za-z0-9-]{1,63})$/;

const SubDomainLinkTable = ({
    subDomains = [],
    translate,
    isCreatingDomain = false,
    onCreateClicked = () => {},
    onCreateLinksDomainClick = () => {},
    onArchiveClicked = () => {},
    onFavouriteClicked = () => {},
    onCancelCreateDomain = () => {},
    domainsLimit = 50,
    isPayingOrg = false,
    isAgency = false,
    subDomainsPage,
}) => {
    const [newSubdomain, setNewSubdomain] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [wasWorking, setWasWorking] = useState(false);

    /** === Helpers === **/
    const isValidFormat = useCallback(name => SUBDOMAIN_REGEX.test(name), []);

    const isDuplicate = useCallback(
        name => subDomains.some(d => d.subdomain.toLowerCase() === name.toLowerCase() && d.zone === DNS_ZONE),
        [subDomains]
    );

    const validate = useCallback(
        name => {
            const trimmed = name.trim();
            if (!trimmed) return '';
            if (!isValidFormat(trimmed)) return translate(VALIDATION_MESSAGES.INVALID_FORMAT);
            if (isDuplicate(trimmed)) return translate(VALIDATION_MESSAGES.DUPLICATE);
            return '';
        },
        [isValidFormat, isDuplicate, translate]
    );

    useEffect(() => {
        // Handle submit UI
        if (subDomainsPage?.working !== wasWorking) {
            setWasWorking(subDomainsPage?.working);
        }

        if (isCreating && wasWorking && !subDomainsPage?.working) {
            if (!subDomainsPage?.error) {
                setNewSubdomain('');
                setValidationError('');
            }
            setIsCreating(false);
            setWasWorking(false);
        }
    }, [subDomainsPage?.working, subDomainsPage?.error, isCreating, wasWorking]);

    const normalizedRows = useMemo(
        () =>
            subDomains.map(sd => ({
                [TABLE_KEYS.DOMAIN_TYPE]: sd.zone === DNS_ZONE ? DOMAIN_TYPES.SINGULAR : DOMAIN_TYPES.BRANDED,
                [TABLE_KEYS.DOMAIN]: sd.fullDomain,
                [TABLE_KEYS.STATUS]: STATUSES.LIVE,
                isFavourite: Boolean(sd.favourite),
                zone: sd.zone,
                subdomain: sd.subdomain,
                isCreatingDomain: false,
                isPayingOrg,
            })),
        [subDomains, isPayingOrg]
    );

    const newDomainRow = useMemo(() => {
        const duplicate = isDuplicate(newSubdomain);
        const valid = isValidFormat(newSubdomain);
        const rowDisabled = isCreatingDomain && subDomains.length >= domainsLimit;

        const status = (() => {
            if (!isCreatingDomain) return STATUSES.LIVE;
            if (!isPayingOrg && subDomains.length >= domainsLimit) return STATUSES.UPGRADE;
            if (subDomains.length >= domainsLimit) return STATUSES.LIMIT_REACHED;
            return STATUSES.EDIT;
        })();

        return {
            [TABLE_KEYS.DOMAIN_TYPE]: DOMAIN_TYPES.SINGULAR,
            [TABLE_KEYS.DOMAIN]: '',
            [TABLE_KEYS.STATUS]: status,
            isFavourite: false,
            zone: DNS_ZONE,
            subdomain: newSubdomain,
            trClass: !isPayingOrg ? css.openTableCell : '',
            isCreatingDomain: true,
            isLoading: subDomainsPage?.working,
            isCreatingButtonDisabled: duplicate || !valid,
            isRowDisabled: rowDisabled,
            underRowComponent: () =>
                rowDisabled ? (
                    <UnderRow
                        actionButtonText={
                            !isPayingOrg ? translate('STATIC.PAGES.MANAGE_LINKS.SUB_DOMAIN.TABLE.CONTACT_SALES') : ''
                        }
                        onButtonClick={() => route('react/account-plan#upgrade-plan')}
                        type={TTypeEnum.ALERT}
                        message={getAlertMessage(subDomains, domainsLimit)}
                    />
                ) : null,
        };
    }, [
        newSubdomain,
        isDuplicate,
        isValidFormat,
        isPayingOrg,
        subDomains.length,
        subDomainsPage?.working,
        domainsLimit,
        isCreatingDomain,
    ]);

    const tableData = useMemo(() => (isCreatingDomain ? [newDomainRow, ...normalizedRows] : normalizedRows), [
        isCreatingDomain,
        newDomainRow,
        normalizedRows,
    ]);

    const handleSubdomainChange = useCallback(
        value => {
            setNewSubdomain(value);
            setValidationError(validate(value));
        },
        [validate]
    );

    const handleCreateClick = useCallback(() => {
        if (!isCreatingDomain) return;

        const trimmed = newSubdomain.trim();
        if (!trimmed) {
            setValidationError(translate(VALIDATION_MESSAGES.REQUIRED));
            return;
        }

        const error = validate(trimmed);
        if (error) {
            setValidationError(error);
            return;
        }

        setIsCreating(true);
        setWasWorking(false);
        onCreateClicked(trimmed, DNS_ZONE);
    }, [isCreatingDomain, newSubdomain, validate, onCreateClicked, translate]);

    const handleCancelCreate = useCallback(() => {
        setNewSubdomain('');
        setValidationError('');
        setIsCreating(false);
        setWasWorking(false);
        onCancelCreateDomain();
    }, [onCancelCreateDomain]);

    const tableMetadata = useMemo(
        () =>
            tableColumns(
                onArchiveClicked,
                onFavouriteClicked,
                handleCreateClick,
                handleCancelCreate,
                handleSubdomainChange,
                validationError,
                isAgency,
                subDomainsPage,
                translate
            ),
        [
            onArchiveClicked,
            onFavouriteClicked,
            handleCreateClick,
            handleCancelCreate,
            handleSubdomainChange,
            validationError,
            isAgency,
            subDomainsPage,
            translate,
        ]
    );

    return (
        <>
            <Table
                showInnerBorder={false}
                trClass={css.trCell}
                thClass={css.thClass}
                zebraTable={false}
                data={tableData}
                metadata={tableMetadata}
            />
            {!tableData.length && (
                <div className={css.emptyContainer}>
                    <Icon onClick={onCreateLinksDomainClick} className={css.icon} name="link-plus" />
                    <Translate id="STATIC.PAGES.MANAGE_LINKS.SUBDOMAIN_SHELF_EMPTY" />
                </div>
            )}
        </>
    );
};

SubDomainLinkTable.propTypes = {
    subDomains: PropTypes.arrayOf(
        PropTypes.shape({
            favourite: PropTypes.bool,
            zone: PropTypes.string,
            fullDomain: PropTypes.string,
            subdomain: PropTypes.string,
        })
    ),
    translate: PropTypes.func.isRequired,
    isCreatingDomain: PropTypes.bool,
    onCreateClicked: PropTypes.func,
    onCreateLinksDomainClick: PropTypes.func,
    onArchiveClicked: PropTypes.func,
    onFavouriteClicked: PropTypes.func,
    onCancelCreateDomain: PropTypes.func,
    domainsLimit: PropTypes.number,
    isAgency: PropTypes.bool,
    isPayingOrg: PropTypes.bool,
    subDomainsPage: PropTypes.object,
};

export default withLocalize(SubDomainLinkTable);
